import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@material-ui/core";
import { useRecordContext } from "react-admin";
import { hostname as apiHost } from "../../providers/api";
import { useTokenOps } from "../../components/TokenContext";
import EcomReferenceConflicts from "./EcomReferenceConflicts";

// Diagnostics of the external API (GET /externalApi/articles) for one account.
// Backend: GET /apiIntegrationDiagnostics/:accountId/status and /test (admin only).
// The API key is never sent to the browser.

const SEVERITY_STYLE = {
  ok: { label: "OK", background: "#2e7d32" },
  warning: { label: "Warning", background: "#ed6c02" },
  error: { label: "Error", background: "#d32f2f" },
  info: { label: "Info", background: "#0277bd" },
};

const SeverityChip = ({ severity }) => {
  const style = SEVERITY_STYLE[severity] || SEVERITY_STYLE.info;
  return (
    <Chip
      size="small"
      label={style.label}
      style={{
        background: style.background,
        color: "white",
        height: 18,
        fontSize: 11,
        minWidth: 52,
      }}
    />
  );
};

const CheckList = ({ checks }) => (
  <Table size="small">
    <TableBody>
      {checks.map((check, index) => (
        <TableRow key={`${check.key}-${index}`}>
          <TableCell style={{ width: 64 }}>
            <SeverityChip severity={check.severity} />
          </TableCell>
          <TableCell>{check.message}</TableCell>
        </TableRow>
      ))}
    </TableBody>
  </Table>
);

// app_feature.key of the client app integration section (Digitization → Ecommerce).
export const INTEGRATION_FEATURE_KEY = "integration";

// The two independent switches, shown apart from the rest of the checks.
const ACCESS_CHECKS = [
  {
    key: "integration_feature",
    title: "Integration",
    detail: "Gives the client access to the Integration section in the app.",
  },
  {
    key: "api_integration_enabled",
    title: "API Integration (Side Lateral)",
    detail: "Lets the client use the API and make calls.",
  },
];

const AccessSummary = ({ checks }) => {
  const byKey = Object.fromEntries(checks.map((check) => [check.key, check]));
  const featureOff = byKey.integration_feature && byKey.integration_feature.severity !== "ok";
  return (
    <Box mb={2}>
      {featureOff && (
        <Box
          mb={1}
          p={1.5}
          style={{ background: "#f5f5f5", border: "1px solid #e0e0e0", borderRadius: 4, color: "rgba(0,0,0,0.6)" }}
        >
          The &quot;Integration&quot; feature is not enabled for this account.
        </Box>
      )}
      <Table size="small">
        <TableBody>
          {ACCESS_CHECKS.map(({ key, title, detail }) => {
            const check = byKey[key];
            if (!check) return null;
            return (
              <TableRow key={key}>
                <TableCell style={{ width: 64 }}>
                  <SeverityChip severity={check.severity} />
                </TableCell>
                <TableCell style={{ width: 220, fontWeight: 500 }}>{title}</TableCell>
                <TableCell>
                  <Typography variant="body2" color="textSecondary">
                    {detail}
                  </Typography>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </Box>
  );
};

const formatDate = (value) => (value ? new Date(value).toLocaleDateString() : "");

const responseError = async (response) => {
  const body = await response.json().catch(() => ({}));
  const message = typeof body.message === "string" ? body.message : response.statusText;
  return new Error(`Request failed (${response.status}): ${message}`);
};

const AccountApiIntegration = ({ accountId: accountIdProp }) => {
  const record = useRecordContext();
  const accountId = accountIdProp || record?.id;
  const tokenOps = useTokenOps();
  const token = tokenOps.account?.accessToken;

  const [status, setStatus] = useState(null);
  const [statusError, setStatusError] = useState(null);
  const [loadingStatus, setLoadingStatus] = useState(false);
  const [testHostname, setTestHostname] = useState("");
  const [testReferences, setTestReferences] = useState("");
  const [testResult, setTestResult] = useState(null);
  const [testError, setTestError] = useState(null);
  const [testing, setTesting] = useState(false);
  // Ignore responses that arrive after switching to another account.
  const currentAccount = useRef(accountId);
  currentAccount.current = accountId;

  const loadStatus = useCallback(async () => {
    if (!accountId) return;
    const requestedFor = accountId;
    setLoadingStatus(true);
    setStatusError(null);
    try {
      const response = await fetch(`${apiHost}/apiIntegrationDiagnostics/${accountId}/status`, {
        headers: { authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw await responseError(response);
      const data = await response.json();
      if (currentAccount.current !== requestedFor) return;
      setStatus(data);
      setTestHostname((previous) => previous || data.hostnames?.[0]?.hostname || "");
    } catch (error) {
      if (currentAccount.current === requestedFor) setStatusError(error.message);
    } finally {
      if (currentAccount.current === requestedFor) setLoadingStatus(false);
    }
  }, [accountId, token]);

  // Reset only when the inspected account changes (not on token refresh).
  useEffect(() => {
    setStatus(null);
    setTestResult(null);
    setTestError(null);
    setTestHostname("");
    setTestReferences("");
  }, [accountId]);

  useEffect(() => {
    loadStatus();
  }, [loadStatus]);

  const runTest = async () => {
    const requestedFor = accountId;
    setTesting(true);
    setTestError(null);
    setTestResult(null);
    try {
      const params = new URLSearchParams();
      // Sent untrimmed: the real API does not trim either.
      if (testHostname) params.set("hostname", testHostname);
      if (testReferences) params.set("references", testReferences);
      const response = await fetch(
        `${apiHost}/apiIntegrationDiagnostics/${accountId}/test?${params.toString()}`,
        { headers: { authorization: `Bearer ${token}` } },
      );
      if (!response.ok) throw await responseError(response);
      const data = await response.json();
      if (currentAccount.current === requestedFor) setTestResult(data);
    } catch (error) {
      if (currentAccount.current === requestedFor) setTestError(error.message);
    } finally {
      if (currentAccount.current === requestedFor) setTesting(false);
    }
  };

  if (!accountId) return null;

  return (
    <Box py={2}>
      <Box display="flex" alignItems="center" mb={1}>
        <Typography variant="h6" style={{ flex: 1 }}>
          Configuration status
        </Typography>
        <Button variant="outlined" color="primary" onClick={loadStatus} disabled={loadingStatus}>
          Refresh
        </Button>
      </Box>
      {loadingStatus && <CircularProgress size={24} />}
      {statusError && <Typography color="error">{statusError}</Typography>}
      {status && (
        <>
          <AccessSummary checks={status.checks} />
          <CheckList
            checks={status.checks.filter((check) => !ACCESS_CHECKS.some(({ key }) => key === check.key))}
          />

          {status.hostnames.length > 0 && (
            <Box mt={3}>
              <Typography variant="subtitle1">Hostnames</Typography>
              <Typography variant="body2" color="textSecondary">
                The API finds the account whose e-commerce domain contains the hostname sent by the client.
              </Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Hostname</TableCell>
                    <TableCell>Matching active accounts</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {status.hostnames.map((item) => (
                    <TableRow key={item.hostname}>
                      <TableCell>
                        {!item.resolvesToThisAccount && <SeverityChip severity="error" />} {item.hostname}
                      </TableCell>
                      <TableCell>
                        {item.matchingAccounts.map((account) => account.name).join(", ") || "None"}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          )}

          <Box mt={3}>
            <Typography variant="subtitle1">E-commerce references by project</Typography>
            <Typography variant="body2" color="textSecondary">
              Only references whose article has calculated metrics can be served. {" "}
              {status.ecomReferences.sharedAcrossProjects > 0 &&
                `${status.ecomReferences.sharedAcrossProjects} references are loaded in more than one project.`}
            </Typography>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Project</TableCell>
                  <TableCell align="right">Articles</TableCell>
                  <TableCell align="right">Ecom references</TableCell>
                  <TableCell align="right">With metrics</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {status.ecomReferences.projects.map((project) => (
                  <TableRow key={project.projectId}>
                    <TableCell>{project.projectName}</TableCell>
                    <TableCell align="right">{project.articles}</TableCell>
                    <TableCell
                      align="right"
                      style={project.ecomReferences === 0 && project.articles > 0 ? { color: "#ed6c02" } : undefined}
                    >
                      {project.ecomReferences}
                    </TableCell>
                    <TableCell align="right">{project.ecomReferencesWithMetrics}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>

          {status.ecomReferences.sharedAcrossProjects > 0 && (
            <EcomReferenceConflicts key={accountId} accountId={accountId} token={token} />
          )}
        </>
      )}

      <Box mt={4}>
        <Typography variant="h6">Test a request</Typography>
        <Typography variant="body2" color="textSecondary">
          Simulates GET /externalApi/articles as the client would call it, assuming the signature is correct.
        </Typography>
        <Box display="flex" alignItems="center" mt={1} style={{ gap: 16 }}>
          <TextField
            id="api-integration-test-hostname"
            label="hostname"
            value={testHostname}
            onChange={(event) => setTestHostname(event.target.value)}
            size="small"
            variant="outlined"
          />
          <TextField
            id="api-integration-test-references"
            label="E-commerce references"
            helperText="Comma-separated e-commerce references, as sent by the client (not the article reference)"
            style={{ minWidth: 320 }}
            value={testReferences}
            onChange={(event) => setTestReferences(event.target.value)}
            size="small"
            variant="outlined"
          />
          <Button variant="contained" color="primary" onClick={runTest} disabled={testing}>
            {testing ? <CircularProgress size={20} /> : "Test"}
          </Button>
        </Box>
        {testError && <Typography color="error">{testError}</Typography>}
        {testResult && (
          <Box mt={2}>
            <Typography variant="subtitle1">
              Expected response: HTTP {testResult.expectedResponse.httpStatus}
            </Typography>
            <Typography variant="body2">{testResult.expectedResponse.summary}</Typography>
            <Typography variant="body2" color="textSecondary">
              Text to sign: <code>{testResult.request.signingString}</code>
            </Typography>
            <Box mt={1}>
              <CheckList checks={testResult.steps} />
            </Box>
            {testResult.candidates.length > 0 && (
              <Box mt={2}>
                <Typography variant="subtitle1">Articles with these references</Typography>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell />
                      <TableCell>Ecom reference</TableCell>
                      <TableCell>Reference</TableCell>
                      <TableCell>Project</TableCell>
                      <TableCell>Account</TableCell>
                      <TableCell>Created</TableCell>
                      <TableCell>Metrics</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {testResult.candidates.map((candidate) => (
                      <TableRow
                        key={`${candidate.articleId}-${candidate.ecomReference}`}
                        style={candidate.served ? { background: "#e8f5e9" } : undefined}
                      >
                        <TableCell>{candidate.served ? <Chip size="small" label="Served" color="primary" /> : null}</TableCell>
                        <TableCell>{candidate.ecomReference}</TableCell>
                        <TableCell>{candidate.reference}</TableCell>
                        <TableCell>{candidate.projectName}</TableCell>
                        <TableCell>{candidate.accountName}</TableCell>
                        <TableCell>{formatDate(candidate.createdAt)}</TableCell>
                        <TableCell>{candidate.hasMetrics ? "Yes" : "No"}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default AccountApiIntegration;
